import { CompanionJobResponse, CompanionJobChanges, OperationPayloadSingleItem, OperationPayloadListItems } from './companion.interface';
import { CompanionOperation } from './companion.enum';
import { SkillType } from '../../enums/skill-type.enum';

/**
 * Generates the changes in the job response for the companion to have context about the changes
 * @param original The original job response
 * @param updated The updated job response
 * @returns The changes in the job response
 */
export function generateCompanionJobChanges(
  original: CompanionJobResponse,
  updated: CompanionJobResponse
): CompanionJobChanges {
  const changes: CompanionJobChanges = {};

  // Compare general properties
  const generalChanges: any = {};
  
  // Simple string/number properties
  const generalSimpleProps: (keyof CompanionJobResponse['general'])[] = [
    'jobTitle', 'workExperience', 'careerLevel', 'fieldOfActivity',
    'startAsSoonAsPossible', 'startDate', 'salary', 'salaryMin', 'salaryMax',
    'salaryInterval', 'languageCode'
  ];

  generalSimpleProps.forEach(prop => {
    if (original.general[prop] !== updated.general[prop]) {
      generalChanges[prop] = {
        newValue: updated.general[prop],
        operation: CompanionOperation.UPDATE
      } as OperationPayloadSingleItem<any>;
    }
  });

  if (Object.keys(generalChanges).length > 0) {
    changes.general = generalChanges;
  }

  // Compare facilities
  const facilitiesChanges: any = {};
  
  if (original.facilities.remote !== updated.facilities.remote) {
    facilitiesChanges.remote = {
      newValue: updated.facilities.remote,
      operation: CompanionOperation.UPDATE
    } as OperationPayloadSingleItem<boolean>;
  }

  // Compare facility IDs arrays
  const originalIds = original.facilities.ids || [];
  const updatedIds = updated.facilities.ids || [];
  
  if (JSON.stringify(originalIds.sort()) !== JSON.stringify(updatedIds.sort())) {
    const idChanges: OperationPayloadListItems<number>[] = [];
    
    // Find added IDs
    updatedIds.forEach(id => {
      if (!originalIds.includes(id)) {
        idChanges.push({
          newValue: id,
          operation: CompanionOperation.ADD
        });
      }
    });
    
    // Find removed IDs
    originalIds.forEach(id => {
      if (!updatedIds.includes(id)) {
        idChanges.push({
          oldValue: id,
          operation: CompanionOperation.DELETE
        });
      }
    });
    
    if (idChanges.length > 0) {
      facilitiesChanges.ids = idChanges;
    }
  }

  if (Object.keys(facilitiesChanges).length > 0) {
    changes.facilities = facilitiesChanges;
  }

  // Compare skills
  const skillsChanges: any = {};
  
  // Compare languages
  const languageChanges = compareSkillItems(
    original.skills.languages || [],
    updated.skills.languages || []
  );
  if (languageChanges.length > 0) {
    skillsChanges.languages = languageChanges;
  }
  
  // Compare skills
  const skillChanges = compareSkillItems(
    original.skills.skills || [],
    updated.skills.skills || []
  );
  if (skillChanges.length > 0) {
    skillsChanges.skills = skillChanges;
  }

  if (Object.keys(skillsChanges).length > 0) {
    changes.skills = skillsChanges;
  }

  // Compare questions
  const originalQuestions = original.questions || [];
  const updatedQuestions = updated.questions || [];
  
  if (JSON.stringify(originalQuestions.sort()) !== JSON.stringify(updatedQuestions.sort())) {
    const questionChanges: OperationPayloadListItems<string>[] = [];
    
    // Find added questions
    updatedQuestions.forEach(question => {
      if (!originalQuestions.includes(question)) {
        questionChanges.push({
          newValue: question,
          operation: CompanionOperation.ADD
        });
      }
    });
    
    // Find removed questions
    originalQuestions.forEach(question => {
      if (!updatedQuestions.includes(question)) {
        questionChanges.push({
          oldValue: question,
          operation: CompanionOperation.DELETE
        });
      }
    });
    
    if (questionChanges.length > 0) {
      changes.questions = questionChanges;
    }
  }

  // Compare description (single markdown field)
  const originalDesc = original.description ?? '';
  const updatedDesc = updated.description ?? '';
  if (originalDesc !== updatedDesc) {
    changes.description = {
      newValue: updatedDesc,
      operation: CompanionOperation.UPDATE,
    } as OperationPayloadSingleItem<string>;
  }

  // Compare requirementISA
  const originalISA = original.requirementISA || [];
  const updatedISA = updated.requirementISA || [];
  
  if (JSON.stringify(originalISA.sort()) !== JSON.stringify(updatedISA.sort())) {
    changes.requirementISA = {
      newValue: updatedISA,
      operation: CompanionOperation.UPDATE
    } as OperationPayloadSingleItem<number[]>;
  }

  return changes;
}

type SkillItem<TLevel> = {
  name: string;
  level: TLevel;
  skillType: SkillType;
};

// Helper function to compare skill items (languages and skills)
function compareSkillItems<TLevel>(
  original: SkillItem<TLevel>[],
  updated: SkillItem<TLevel>[]
): OperationPayloadListItems<SkillItem<TLevel>>[] {
  const changes: OperationPayloadListItems<SkillItem<TLevel>>[] = [];
  
  // Create maps for easier comparison
  const originalMap = new Map(original.map(item => [item.name, item]));
  const updatedMap = new Map(updated.map(item => [item.name, item]));
  
  // Find added or updated items
  updatedMap.forEach((updatedItem, name) => {
    const originalItem = originalMap.get(name);
    
    if (!originalItem) {
      // Item was added
      changes.push({
        newValue: updatedItem,
        operation: CompanionOperation.ADD
      });
    } else if (
      originalItem.level !== updatedItem.level ||
      originalItem.skillType !== updatedItem.skillType
    ) {
      // Item was updated
      changes.push({
        oldValue: originalItem,
        newValue: updatedItem,
        operation: CompanionOperation.UPDATE
      });
    }
  });
  
  // Find removed items
  originalMap.forEach((originalItem, name) => {
    if (!updatedMap.has(name)) {
      changes.push({
        oldValue: originalItem,
        operation: CompanionOperation.DELETE
      });
    }
  });
  
  return changes;
}